import { useState } from "react";
import { Layers, Plus, Trash2, Check, Loader2, Pencil, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { createGroup, updateGroup, deleteGroup } from "@/lib/chatApi";

export default function GroupManager({ rooms, groups, token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [picked, setPicked] = useState([]);
  const [creating, setCreating] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editRooms, setEditRooms] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const roomName = (id) => rooms.find((r) => r.id === id)?.name || id;

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Nama group wajib diisi");
      return;
    }
    setCreating(true);
    try {
      await createGroup(token, name.trim(), desc.trim(), picked);
      setName("");
      setDesc("");
      setPicked([]);
      onChanged?.();
      toast.success("Group berhasil dibuat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat group");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (g) => {
    setEditId(g.id);
    setEditRooms(g.room_ids || []);
  };

  const handleSaveEdit = async (g) => {
    setSavingId(g.id);
    try {
      await updateGroup(token, g.id, { room_ids: editRooms });
      setEditId(null);
      onChanged?.();
      toast.success("Group diperbarui");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui group");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (g) => {
    setDeletingId(g.id);
    try {
      await deleteGroup(token, g.id);
      if (editId === g.id) setEditId(null);
      onChanged?.();
      toast.success("Group dihapus");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus group");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="manage-group-button"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
        >
          <Layers className="w-4 h-4" /> Kelola Group
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-emerald-500" /> Kelola Group</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Buat Group Baru</p>
          <input
            data-testid="new-group-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Nama group (mis. "FamilyFx Group")'
            className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <input
            data-testid="new-group-desc-input"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Deskripsi (opsional)"
            className="w-full px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">Pilih room untuk dimasukkan:</p>
            <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
              {rooms.length === 0 && <p className="text-xs text-slate-400 p-3">Belum ada room.</p>}
              {rooms.map((r) => (
                <label
                  key={r.id}
                  data-testid={`new-group-room-${r.id}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60"
                >
                  <span className={`w-4 h-4 rounded flex items-center justify-center border ${picked.includes(r.id) ? "bg-emerald-500 border-emerald-500" : "border-slate-300 dark:border-slate-600"}`}>
                    {picked.includes(r.id) && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <input type="checkbox" className="hidden" checked={picked.includes(r.id)} onChange={() => toggle(picked, setPicked, r.id)} />
                  <span className="truncate">{r.name}</span>
                </label>
              ))}
            </div>
          </div>
          <button
            data-testid="create-group-submit"
            onClick={handleCreate}
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm transition-all disabled:opacity-60"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {creating ? "Membuat..." : "Buat Group"}
          </button>
        </div>

        <div className="pt-3 mt-1 border-t border-slate-200 dark:border-slate-800 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Group yang Ada</p>
          {groups.length === 0 && <p className="text-xs text-slate-400 py-2">Belum ada group.</p>}
          {groups.map((g) => (
            <div key={g.id} data-testid={`group-row-${g.id}`} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{g.name}</p>
                  <p className="text-xs text-slate-400 truncate">{(g.room_ids || []).length} room · ID: {g.id.slice(0, 8)}</p>
                </div>
                <button
                  data-testid={`group-edit-${g.id}`}
                  onClick={() => (editId === g.id ? setEditId(null) : startEdit(g))}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                  title="Ubah room"
                >
                  {editId === g.id ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                </button>
                <button
                  data-testid={`group-delete-${g.id}`}
                  onClick={() => handleDelete(g)}
                  disabled={deletingId === g.id}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                  title="Hapus group"
                >
                  {deletingId === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>

              {editId === g.id && (
                <div className="space-y-2">
                  <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                    {rooms.map((r) => (
                      <label
                        key={r.id}
                        data-testid={`edit-group-room-${g.id}-${r.id}`}
                        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60"
                      >
                        <span className={`w-4 h-4 rounded flex items-center justify-center border ${editRooms.includes(r.id) ? "bg-indigo-500 border-indigo-500" : "border-slate-300 dark:border-slate-600"}`}>
                          {editRooms.includes(r.id) && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <input type="checkbox" className="hidden" checked={editRooms.includes(r.id)} onChange={() => toggle(editRooms, setEditRooms, r.id)} />
                        <span className="truncate">{r.name}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    data-testid={`group-save-${g.id}`}
                    onClick={() => handleSaveEdit(g)}
                    disabled={savingId === g.id}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all disabled:opacity-60"
                  >
                    {savingId === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Simpan Room
                  </button>
                </div>
              )}

              {editId !== g.id && (g.room_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {(g.room_ids || []).map((id) => (
                    <span key={id} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                      {roomName(id)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
